import { Button, Col, Card, CardBody } from "react-bootstrap";
import languageES from "datatables.net-plugins/i18n/es-MX";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import api from "@/api/api.js";
import $ from "jquery";
import "datatables.net";
import { capitalizeFirstLetter } from "@/utils/stringUtils.js";
import { Link, useNavigate } from "react-router-dom";

const Bills = () => {
  const { t } = useTranslation();
  const userId = sessionStorage.getItem("user");
  const tableRef = useRef(null);
  const dataTable = useRef(null);
  const [billsAvailable, setBillsAvailable] = useState(0);
  const [showCreateButton, setShowCreateButton] = useState(false);

  const navigate = useNavigate();

  const BILLING_STATUS_VARIANT = {
    1: "secondary", // REQUESTED
    2: "success", // APPROVED
    3: "danger", // REJECTED
    4: "warning", // REQUESTED_TO_CANCEL
  };

  const renderStatusBadge = (status, label) => {
    const variant = BILLING_STATUS_VARIANT[status] ?? "dark";

    return `
            <span class="badge bg-${variant}">
                ${label}
            </span>
        `;
  };

  const fetchAvailablePackages = async () => {
    try {
      const response = await api.get(`/billings/available-count`);
      setBillsAvailable(response.data.count);
      setShowCreateButton(response.data.count > 0);
    } catch {
      setShowCreateButton(false);
    }
  };

  useEffect(() => {
    if (!tableRef.current || !userId) return;

    fetchAvailablePackages();

    const STYLE_ID = "bills-row-style";
    if (!document.getElementById(STYLE_ID)) {
      const styleEl = document.createElement("style");
      styleEl.id = STYLE_ID;
      styleEl.textContent = `
        .bill-status-1 td { background-color: #fff8e1 !important; }
        .bill-status-2 td { background-color: #e8f5e9 !important; }
        .bill-status-3 td { background-color: #fdecea !important; }
        .bill-status-4 td { background-color: #fff3cd !important; }
        .bill-status-5 td { background-color: #eeeeee !important; }
      `;
      document.head.appendChild(styleEl);
    }

    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().clear().destroy();
      $(tableRef.current).empty();
    }

    dataTable.current = $(tableRef.current).DataTable({
      language: languageES,
      processing: true,
      serverSide: true,
      createdRow: (row, data) => {
        if (data?.billingStatus)
          $(row).addClass(`bill-status-${data.billingStatus}`);
      },
      ajax: async (dtParams, callback) => {
        try {
          const order = dtParams.order[0];
          const columnName = dtParams.columns[order.column].name;
          const response = await api.get(
            `/billings/user/${userId}/all?requestedBy=not_null`,
            {
              params: {
                page: dtParams.start / dtParams.length + 1,
                itemsPerPage: dtParams.length,
                [`order[${columnName}]`]: order.dir,
                search: dtParams.search?.value || undefined,
              },
            },
          );
          callback({
            draw: dtParams.draw,
            recordsTotal: response.data["hydra:totalItems"],
            recordsFiltered: response.data["hydra:totalItems"],
            data: response.data["hydra:member"],
          });
        } catch {
          callback({
            draw: dtParams.draw,
            recordsTotal: 0,
            recordsFiltered: 0,
            data: [],
          });
        }
      },
      columns: [
        {
          title: capitalizeFirstLetter(t("folio")),
          data: "id",
          name: "id",
        },
        {
          title: capitalizeFirstLetter(t("requested by")),
          render: (data, type, row) =>
            row?.requestedBy?.userProfile?.fullName ?? "",
          orderable: false,
        },
        {
          title: capitalizeFirstLetter(t("requested to")),
          render: (data, type, row) => row?.contact?.businessName ?? "",
          orderable: false,
        },
        {
          title: capitalizeFirstLetter(t("requested")),
          data: "createdAtAgo",
          name: "updatedAt",
        },
        {
          title: capitalizeFirstLetter(t("status")),
          data: "billingStatus",
          name: "billingStatus",
          render: (data, type, row) =>
            renderStatusBadge(row.billingStatus, row.billingStatusLabel),
        },
        {
          title: capitalizeFirstLetter(t("acciones")),
          render: (data, type, row) => `
                        <button
                            class="btn btn-warning btn-detail-billing"
                            data-id="${row?.id ?? ""}"
                        >
                            Detalle
                        </button>
                    `,
          orderable: false,
        },
      ],
    });

    const handleShowClick = function () {
      const billingId = $(this).data("id");
      navigate(`/bills/${billingId}/show`);
    };

    $(tableRef.current).on("click", ".btn-detail-billing", handleShowClick);

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().clear().destroy();
      }

      $(tableRef.current).off("click", ".btn-detail-billing", handleShowClick);
    };
  }, [userId, navigate, t]);

  return (
    <>
      <Col xs={12}>
        <Card>
          <Card.Header>{t("ui.requestedBillsList")}</Card.Header>
          <CardBody>
            <table
              ref={tableRef}
              className="display"
              style={{ width: "100%" }}
            />

            <div
              style={{ visibility: showCreateButton ? "visible" : "hidden" }}
            >
              <Link
                to={billsAvailable > 0 ? `/bills/create` : "#"}
                style={{
                  textDecoration: "none",
                  pointerEvents: billsAvailable > 0 ? "auto" : "none",
                }}
              >
                <Button
                  variant="primary"
                  className="mb-3"
                  disabled={billsAvailable === 0}
                >
                  {t("ui.requestBill")}
                  <br />
                  <span>{billsAvailable} disponibles</span>
                </Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default Bills;
